import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Col, Modal, Row } from "react-bootstrap";
import Search from "../../../../components/Search";
import AlertMessage from "../../../../components/AlertMessage";
import { useAlert } from "../../../../hooks/useAlert";
import { useUserPermissions } from "../../../../utils/auth";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import AlertValuesTable from "../components/AlertValuesTable";
import { AlertConfigApiItem, AlertValuesListFields } from "../types";
import { Pagination } from "../../../../types/Pagination";
import { PERMISSIONS } from "../../../../constants/permissions";
import { deleteAlertConfig, getAlertConfigs } from "../api/alertValuesApi";
import { formatDate } from "../../../../utils/formatters/DateFormatter";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";

// TODO: Refactor. Para añadir un nueva columna a la tabla se debe tocar en demasiados sitios.
export default function AlertValuesList() {
    const { t } = useTranslation(["menu", "claim", "common"]);
    const permissions = useUserPermissions();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { alert, hideAlert, isVisible, showAlert } = useAlert({ fromLocation: true });
    const [searchTerm, setSearchTerm] = useState("");
    const [appliedSearchText, setAppliedSearchText] = useState("");
    const [alertConfigs, setAlertConfigs] = useState<AlertConfigApiItem[]>([]);
    const [alertConfigToDelete, setAlertConfigToDelete] = useState<AlertValuesListFields | null>(null);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);

    const loadAlertConfigs = useCallback(async () => {
        try {
            const response = await getAlertConfigs();
            setAlertConfigs(response ?? []);
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            showAlert({
                message: fetchError?.message ?? t("common:requestError.errorBadRequest"),
                type: "danger",
                duration: 8000
            });
        }
    }, [showAlert, t]);

    useEffect(() => {
        loadAlertConfigs();
    }, [loadAlertConfigs]);

    const alertValuesData: Pagination<AlertValuesListFields> = useMemo(() => {
        const items = alertConfigs.map((item) => ({
            id: item.id,
            value: item.value,
            alertType: item.generatesAlert ? t("claim:withAlert") : t("claim:withoutAlert"),
            alertReference: "",
            validStartDate: formatDate(item.startDate),
            validEndDate: formatDate(item.endDate),
            criteria: item.criteria
        }));

        return {
            page: 1,
            size: items.length,
            totalPages: 1,
            totalRecords: items.length,
            items
        };
    }, [alertConfigs, t]);

    const filteredAlertValuesData: Pagination<AlertValuesListFields> = useMemo(() => {
        if (!appliedSearchText.trim()) {
            return alertValuesData;
        }

        const normalizedSearch = appliedSearchText.trim().toLowerCase();
        const filteredItems = alertValuesData.items.filter((item) => {
            const valueMatch = item.value.toString().includes(normalizedSearch);
            const typeMatch = item.alertType.toLowerCase().includes(normalizedSearch);
            return valueMatch || typeMatch;
        });

        return {
            ...alertValuesData,
            size: filteredItems.length,
            totalRecords: filteredItems.length,
            items: filteredItems
        };
    }, [appliedSearchText, alertValuesData]);

    const handleSearchInput = () => {
        setAppliedSearchText(searchTerm);
    };

    const handleClear = () => {
        setSearchTerm("");
        setAppliedSearchText("");
    };

    const handleDeleteClick = (item: AlertValuesListFields) => {
        setAlertConfigToDelete(item);
    };

    const handleEditClick = (id: number) => {
        navigate(`/admin/alertValues/edit/${id}`);
    };

    const handleCloseDeleteModal = () => {
        if (isDeleteLoading) {
            return;
        }
        setAlertConfigToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!alertConfigToDelete) {
            return;
        }

        setIsDeleteLoading(true);
        try {
            await deleteAlertConfig(alertConfigToDelete.id);
            setAlertConfigToDelete(null);
            queryClient.invalidateQueries({ queryKey: ['alertConfigs'] });
            await loadAlertConfigs();
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            showAlert({
                message: fetchError?.message ?? t("common:requestError.errorBadRequest"),
                type: "danger",
                duration: 8000
            });
        } finally {
            setIsDeleteLoading(false);
        }
    };

    return (
        <>
            <Modal show={!!alertConfigToDelete} onHide={handleCloseDeleteModal}>
                <Modal.Body>
                    {alertConfigToDelete &&
                        t("claim:modal.alertValuesDeleteConfirm", {
                            value: alertConfigToDelete.value,
                            alertType: alertConfigToDelete.alertType
                        })}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={isDeleteLoading}>
                        {t("common:buttons.buttonNo")}
                    </Button>
                    <Button variant="primary" onClick={handleConfirmDelete} disabled={isDeleteLoading}>
                        {t("common:buttons.buttonYes")}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Row className="mb-2 align-items-center justify-content-between">
                <Col xs={12} md>
                    <h2>{t("menu:admin.accordeonCriteriaAction2")}</h2>
                </Col>
                {permissions.length > 0 && permissions.includes(PERMISSIONS.ALERT_CONFIG.CREATE) && (
                    <Col xs={12} md="auto" className="d-flex align-items-center justify-content-end mt-2 mt-md-0">
                        <Button
                            variant="primary"
                            style={{ whiteSpace: "nowrap" }}
                            onClick={() => navigate("/admin/alertValues/new-alert-value")}
                        >
                            {t("common:buttons.buttonNewAlertValue")}
                        </Button>
                    </Col>
                )}
            </Row>

            <Row>
                <Col className="text-center" xs={12}>
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert}
                        />
                    )}
                </Col>
            </Row>

            <Row className="mb-2">
                <Col xs={12}>
                    <Search
                        searchText={searchTerm}
                        onChange={(text) => setSearchTerm(text)}
                        onSearch={handleSearchInput}
                        onClear={handleClear}
                    />
                </Col>
            </Row>

            <AlertValuesTable
                alertValuesData={filteredAlertValuesData}
                totalPages={filteredAlertValuesData.totalPages}
                currentPage={filteredAlertValuesData.page}
                pageSize={filteredAlertValuesData.size}
                alertValuesActions={{ onDelete: handleDeleteClick, onView: handleEditClick }}
            />
        </>
    );
}

